import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Badge,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import HomeIcon from "@mui/icons-material/Home";
import MenuIcon from "@mui/icons-material/Menu";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import styles from "./styles.module.css";
import { useContext, useState } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { IAuthContext } from "../../types/auth";
import { DeliveryContext } from "../../contexts/DeliveryContext";
import { IDeliveryContext } from "../../types/delivery";
import { CartContext } from "../../contexts/CartContext";
import { ICartContext } from "../../types/cart";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import usePerfilPU from "../../services/usePerfilPU";

const NavigationBar = () => {
  const { clearAuthTokens, tokens } = useContext(AuthContext) as IAuthContext;
  const { setDeliveryMethodId, setAddress } = useContext(
    DeliveryContext,
  ) as IDeliveryContext;
  const { emptyCart, getCart } = useContext(CartContext) as ICartContext;
  const { data: urbanPartner } = usePerfilPU();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const cart = getCart(slug);
  const totalItems = Object.values(cart).reduce(
    (total, item) => total + item.amount,
    0,
  );

  const handleLogout = () => {
    emptyCart();
    setDeliveryMethodId(null);
    setAddress(null);
    clearAuthTokens();
    queryClient.removeQueries({ queryKey: ["urban-partner"] });
    handleCloseMenu();
  };

  const goToLogin = () => {
    const splittedPathname = location.pathname.split(slug + "/");
    const page = splittedPathname.length > 1 ? splittedPathname[1] : "";
    navigate(`/login?slug=${slug}&page=${page}`);
    handleCloseMenu();
  };

  const goHome = () => {
    navigate(slug ? `/${slug}` : "/");
    handleCloseMenu();
  };

  const goToProfile = () => {
    navigate(slug ? `/${slug}/perfil` : "/perfil");
    handleCloseMenu();
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const openCart = () => {
    if (!slug) {
      return;
    }
    window.dispatchEvent(new Event("open-cart"));
  };

  return (
    <>
      <AppBar position="fixed">
        <Toolbar sx={{ position: "relative" }}>
          <div className={styles.centeredElement}>
            <img
              alt="logo"
              className={styles.logo}
              src="/logo.png"
              style={{ cursor: "pointer" }}
              onClick={goHome}
            />
          </div>
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              gap: 1.5,
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            {isMobile ? (
              <>
                <IconButton
                  color="inherit"
                  aria-label="carrinho"
                  onClick={openCart}
                >
                  <Badge badgeContent={totalItems} color="secondary" showZero>
                    <ShoppingCartIcon />
                  </Badge>
                </IconButton>
                <IconButton
                  color="inherit"
                  aria-label="menu"
                  onClick={handleOpenMenu}
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleCloseMenu}
                >
                  <MenuItem onClick={goHome}>
                    <HomeIcon sx={{ mr: 1 }} />
                    Página inicial
                  </MenuItem>

                  {tokens.access !== null && urbanPartner ? (
                    <MenuItem onClick={goToProfile}>
                      <AccountCircleIcon sx={{ mr: 1 }} />
                      Perfil ({urbanPartner.name})
                    </MenuItem>
                  ) : null}

                  {tokens.access !== null ? (
                    <MenuItem onClick={handleLogout}>
                      <LogoutIcon sx={{ mr: 1 }} />
                      Sair
                    </MenuItem>
                  ) : (
                    <MenuItem onClick={goToLogin}>
                      <LoginIcon sx={{ mr: 1 }} />
                      Entrar
                    </MenuItem>
                  )}
                </Menu>
              </>
            ) : (
              <>
                <IconButton color="inherit" aria-label="home" onClick={goHome}>
                  <HomeIcon />
                </IconButton>
                <IconButton
                  color="inherit"
                  aria-label="carrinho"
                  onClick={openCart}
                >
                  <Badge badgeContent={totalItems} color="secondary" showZero>
                    <ShoppingCartIcon />
                  </Badge>
                </IconButton>
                {tokens.access !== null && urbanPartner ? (
                  <>
                    <IconButton
                      color="inherit"
                      aria-label="perfil"
                      onClick={goToProfile}
                    >
                      <AccountCircleIcon />
                    </IconButton>
                    <Box>Olá, {urbanPartner.name}!</Box>
                  </>
                ) : null}
                {tokens.access !== null ? (
                  <IconButton
                    color="inherit"
                    aria-label="logout"
                    onClick={handleLogout}
                  >
                    <LogoutIcon />
                  </IconButton>
                ) : (
                  <IconButton
                    color="inherit"
                    aria-label="login"
                    onClick={goToLogin}
                  >
                    <LoginIcon />
                  </IconButton>
                )}
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <Toolbar />
    </>
  );
};

export default NavigationBar;
